import type { ActionReducerMapBuilder, PayloadAction } from "@reduxjs/toolkit";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { IReduxRequestNode } from "@litres-libs/types";
import type {
  IAddArtReviewParams,
  IAddArtReviewRequest,
  IAddReviewReplyAction,
  IAddReviewReplyParams,
  IAddReviewReplyRequest,
  IArtsReviewsState,
  IGetArtReviewsParams,
  IGetArtReviewsRequest,
  IGetUserArtsReviewsParams,
  IGetUserArtsReviewsRequest,
  IUserArtsReviewsResponse,
} from "./artsReviews.types";
import { Request } from "../../../../request/Request";
import type { IReview, IReviewsResponse } from "../../reviews/reviews.types";
import {
  artsReviewDislikesCases,
  artsReviewLikesCases,
  updateReviewState,
} from "./artsReviewsSlice.likes";
import { getGlobalHeaders } from "../../../browser/browserSlice";
import type { RootState } from "../../../../store";
import { addHydrateReducer } from "../../../../utils/hydrateReducer";

const initialState: IArtsReviewsState = {
  list: [],
  idMap: {},
  sortType: "popular",
  pagination: {
    next: null,
    previous: null,
  },
  userReview: null,
  lastGetRequestDate: new Date().toString(),
  actions: {
    getArtsReviews: {},
    addArtsReview: {},
    getUserArtsReviews: {},
    addArtReviewLike: {},
    removeArtReviewLike: {},
    addArtReviewDislike: {},
    removeArtReviewDislike: {},
    addReviewReply: {},
  },
};

export const getArtsReviews = createAsyncThunk(
  "global/arts/reviews/getArtsReviews",
  async (params: IGetArtReviewsParams, { getState }) => {
    const { url, o, limit, with_reply_id, with_review_id } = params;
    const response = new Request.Foundation<
      IGetArtReviewsRequest,
      IReviewsResponse
    >({
      url: url ? url : `/api/arts/${params.artId}/reviews`,
      provider: {
        id: "GET_ARTS_REVIEWS",
      },
      request: {
        method: "GET",
        params: {
          o,
          limit,
          with_reply_id,
          with_review_id,
        },
      },
      headers: {
        ...getGlobalHeaders(getState() as RootState),
      },
    });

    return await response.fetch();
  }
);

export const addArtsReview = createAsyncThunk(
  "global/arts/reviews/addArtsReview",
  async (params: IAddArtReviewParams, { getState }) => {
    const response = new Request.Foundation<
      IAddArtReviewRequest,
      IUserArtsReviewsResponse
    >({
      url: `/api/arts/${params.artId}/reviews`,
      provider: {
        id: "ADD_ART_REVIEW",
      },
      request: {
        method: "POST",
        data: {
          text: params.text,
        },
      },
      headers: {
        ...getGlobalHeaders(getState() as RootState),
      },
    });

    return await response.fetch();
  }
);

export const getUserArtsReviews = createAsyncThunk(
  "global/arts/reviews/getUserArtsReviews",
  async (params: IGetUserArtsReviewsParams, { getState }) => {
    const { limit = 10 } = params;
    const response = new Request.Foundation<
      IGetUserArtsReviewsRequest,
      IUserArtsReviewsResponse
    >({
      url: `/api/arts/${params.artId}/user-reviews`,
      provider: {
        id: "GET_USER_ARTS_REVIEWS",
      },
      request: {
        method: "GET",
        params: {
          limit: limit,
          before: params.before,
          after: params.after,
        },
      },
      headers: {
        ...getGlobalHeaders(getState() as RootState),
      },
    });

    return await response.fetch();
  }
);

interface IGetArtReviewsActionMeta {
  arg: IGetArtReviewsParams;
}

const getArtsReviewsCase = (
  builder: ActionReducerMapBuilder<IArtsReviewsState>
): void => {
  // Загрузка
  builder.addCase(getArtsReviews.pending, (state: IArtsReviewsState) => {
    state.actions.getArtsReviews.isLoading = true;
    state.actions.getArtsReviews.error = undefined;
  });
  // Ответ
  builder.addCase(
    getArtsReviews.fulfilled,
    (
      state: IArtsReviewsState,
      action: PayloadAction<
        IReduxRequestNode<IGetArtReviewsRequest, IReviewsResponse>,
        string,
        IGetArtReviewsActionMeta
      >
    ) => {
      state.actions.getArtsReviews.isLoading = false;
      const isPrevious = action.meta.arg.isPrevious;
      const response = action.payload.response;
      const actionSort = action.meta.arg.o;

      if (response?.payload?.data && Array.isArray(response.payload.data)) {
        if (state.sortType !== actionSort) {
          state.idMap = {};
          state.sortType = actionSort;
        }

        if (isPrevious) {
          state.list = [...response.payload.data, ...state.list];
        } else {
          response.payload.data.forEach(function (value) {
            if (!state.idMap[value.id]) {
              state.idMap[value.id] = value.id;
              state.list.push(value);
            }
          });
        }
      }

      if (response?.payload?.pagination) {
        if (typeof isPrevious === "undefined") {
          state.pagination.previous = response.payload.pagination.previous_page;
          state.pagination.next = response.payload.pagination.next_page;
        } else {
          if (isPrevious) {
            state.pagination.previous =
              response.payload.pagination.previous_page;
          } else {
            state.pagination.next = response.payload.pagination.next_page;
          }
        }
      }

      if (action.payload.error) {
        console.error("Ошибка запроса getArtsReviews", action.payload.error);
        state.actions.getArtsReviews.error = action.payload.error;
      }
    }
  );
};

const addArtsReviewCase = (
  builder: ActionReducerMapBuilder<IArtsReviewsState>
): void => {
  // Загрузка
  builder.addCase(addArtsReview.pending, (state: IArtsReviewsState) => {
    state.actions.addArtsReview.isLoading = true;
    state.actions.addArtsReview.error = undefined;
  });
  // Ответ
  builder.addCase(
    addArtsReview.fulfilled,
    (
      state: IArtsReviewsState,
      action: PayloadAction<
        IReduxRequestNode<IAddArtReviewRequest, IUserArtsReviewsResponse>
      >
    ) => {
      state.actions.addArtsReview.isLoading = false;

      if (action.payload.error) {
        console.error("Ошибка запроса addArtsReview", action.payload.error);
        state.actions.addArtsReview.error = action.payload.error;
        return;
      }

      const response = action.payload.response;
      if (response?.payload?.data && !Array.isArray(response.payload.data)) {
        state.userReview = response.payload.data;
        state.idMap[state.userReview["id"]] = state.userReview["id"];
      }
    }
  );
};

const addGetUserArtsReviewsCase = (
  builder: ActionReducerMapBuilder<IArtsReviewsState>
): void => {
  // Загрузка
  builder.addCase(getUserArtsReviews.pending, (state: IArtsReviewsState) => {
    state.actions.getUserArtsReviews.isLoading = true;
    state.actions.getUserArtsReviews.error = undefined;
  });
  // Ответ
  builder.addCase(
    getUserArtsReviews.fulfilled,
    (
      state: IArtsReviewsState,
      action: PayloadAction<
        IReduxRequestNode<IGetUserArtsReviewsRequest, IUserArtsReviewsResponse>
      >
    ) => {
      state.actions.getUserArtsReviews.isLoading = false;

      if (action.payload.error) {
        console.error(
          "Ошибка запроса getUserArtsReviews",
          action.payload.error
        );
        state.actions.getUserArtsReviews.error = action.payload.error;
        return;
      }

      const response = action.payload.response;
      if (
        response?.payload?.data &&
        Array.isArray(response.payload.data) &&
        response.payload.data.length > 0
      ) {
        state.userReview = response.payload.data[0];
      }
    }
  );
};

export const addReviewReply = createAsyncThunk(
  "global/reviews/addReviewReply",
  async (
    params: {
      data: IAddReviewReplyParams;
      onSuccess?: () => void;
      onError?: () => void;
    },
    { getState }
  ) => {
    const { reviewId, text } = params.data;
    const response = new Request.Foundation<
      IAddReviewReplyRequest,
      IReviewsResponse
    >({
      url: `/api/reviews/${reviewId}/replies`,
      provider: {
        id: "ADD_REVIEW_REPLY",
      },
      request: {
        method: "POST",
        data: {
          text: text,
        },
      },
      headers: {
        ...getGlobalHeaders(getState() as RootState),
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

interface IAddReviewActionMeta {
  arg: {
    data: IAddReviewReplyParams;
    onSuccess?(): void;
    onError?(): void;
  };
  requestId: string;
  requestStatus: string;
}

const addReviewReplyCase = (
  builder: ActionReducerMapBuilder<IArtsReviewsState>
): void => {
  // Загрузка
  builder.addCase(addReviewReply.pending, (state: IArtsReviewsState) => {
    state.actions.addReviewReply.isLoading = true;
    state.actions.addReviewReply.error = undefined;
  });
  // Ответ
  builder.addCase(
    addReviewReply.fulfilled,
    (
      state: IArtsReviewsState,
      action: PayloadAction<IAddReviewReplyAction, string, IAddReviewActionMeta>
    ) => {
      const reviewId = action.meta.arg.data.reviewId;

      state.actions.addReviewReply.isLoading = false;

      if (action.payload.error) {
        console.error("Ошибка запроса addReviewReply", action.payload.error);
        state.actions.addReviewReply.error = action.payload.error;
        return;
      }

      const response = action.payload.response;
      if (response?.payload?.data && !Array.isArray(response.payload.data)) {
        updateReviewState(state, reviewId, response);
      }
    }
  );
};

export const artsReviewsSlice = createSlice({
  name: "global/arts/reviews",
  initialState,
  reducers: {
    setArtReviews: (
      state: IArtsReviewsState,
      action: PayloadAction<IReview[]>
    ) => {
      state.list = action.payload;
    },
    setArtReviewsMapId: (
      state: IArtsReviewsState,
      action: PayloadAction<{ [key: number]: number }>
    ) => {
      state.idMap = action.payload;
    },
    setLastGetRequestDate: (
      state: IArtsReviewsState,
      action: PayloadAction<string>
    ) => {
      state.lastGetRequestDate = action.payload;
    },
  },
  extraReducers: builder => {
    addHydrateReducer(builder, "global.arts.reviews");
    getArtsReviewsCase(builder);
    addArtsReviewCase(builder);
    addGetUserArtsReviewsCase(builder);
    artsReviewLikesCases(builder);
    artsReviewDislikesCases(builder);
    addReviewReplyCase(builder);
  },
});

export const { setArtReviews, setLastGetRequestDate, setArtReviewsMapId } =
  artsReviewsSlice.actions;

export default artsReviewsSlice.reducer;
